import { ArrowUpRight, DownloadSimple, EnvelopeSimple, Terminal } from '@phosphor-icons/react';
import type {
  AboutValues,
  ContactValues,
  EducationValues,
  ExperienceValues,
  HeroValues,
  ProjectsValues,
} from '../../schemas';
import { PLATFORM_META } from '../../linkPlatforms';
import type { TemplateProps } from './shared';

export function DevLogTemplate({ draft, enabled, ownerName }: TemplateProps) {
  const contact = draft.contact;
  const about = draft.about;

  return (
    <div className="min-h-full bg-canvas font-mono text-ink">
      <div className="mx-auto max-w-3xl px-8 py-12">
        <header className="flex items-center justify-between border-b border-line pb-4 text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          <div className="flex items-center gap-2">
            <Terminal size={14} className="text-ink-muted" />
            <span>~/{ownerName.toLowerCase().replace(/\s+/g, '-')}</span>
          </div>
          <div className="hidden items-center gap-4 sm:flex">
            <TopLink label="Home" href="#top" />
            {contact?.links?.slice(0, 2).map((l) => {
              const meta = PLATFORM_META[l.platform] ?? PLATFORM_META.custom;
              return <TopLink key={l.id} label={meta.label} href={l.href} external />;
            })}
            {about?.resumeUrl ? <TopLink label="Resume" href={about.resumeUrl} external /> : null}
          </div>
        </header>

        {enabled.has('hero') && draft.hero ? (
          <HeroBlock values={draft.hero} about={about} contact={contact} />
        ) : null}
        {enabled.has('about') && draft.about ? <AboutBlock values={draft.about} /> : null}
        {enabled.has('projects') && draft.projects ? <ProjectsBlock values={draft.projects} /> : null}
        {enabled.has('experience') && draft.experience ? <ExperienceBlock values={draft.experience} /> : null}
        {enabled.has('education') && draft.education ? <EducationBlock values={draft.education} /> : null}
        {enabled.has('contact') && draft.contact ? <ContactBlock values={draft.contact} /> : null}

        <footer className="mt-20 border-t border-line pt-5 text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          <div className="flex items-center justify-between">
            <span>$ echo "built with Portfoli"</span>
            <span>© {new Date().getFullYear()}</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

function TopLink({ label, href, external }: { label: string; href: string; external?: boolean }) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-muted transition-colors hover:text-ink"
    >
      {label}
      {external ? <ArrowUpRight size={10} weight="bold" /> : null}
    </a>
  );
}

function SectionNumber({ num, title }: { num: string; title: string }) {
  return (
    <div className="mt-16 mb-6 flex items-baseline gap-3 border-b border-line pb-3">
      <span className="text-[11px] uppercase tracking-[0.18em] text-ink-faint">{num}</span>
      <h2 className="font-sans text-lg font-semibold tracking-tight text-ink">{title}</h2>
    </div>
  );
}

function HeroBlock({
  values,
  about,
  contact,
}: {
  values: HeroValues;
  about?: AboutValues;
  contact?: ContactValues;
}) {
  const resumeUrl = about?.resumeUrl;
  const email = contact?.email;

  return (
    <section className="pt-12" id="top">
      <div className="flex items-start gap-5">
        {values.portraitUrl ? (
          <img
            src={values.portraitUrl}
            alt=""
            className="h-14 w-14 shrink-0 rounded-full border border-line object-cover"
          />
        ) : null}
        <div className="flex-1">
          {values.tagline ? (
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint">
              $ whoami · {values.tagline}
            </p>
          ) : (
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint">$ whoami</p>
          )}
          <h1 className="mt-3 font-sans text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
            {values.headline}
          </h1>
          {values.subheadline ? (
            <p className="mt-4 max-w-[58ch] font-sans text-base leading-relaxed text-ink-muted">
              {values.subheadline}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {resumeUrl ? (
          <a
            href={resumeUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-2 text-xs font-medium text-board transition-colors hover:bg-ink/90"
          >
            <DownloadSimple size={12} weight="bold" /> Get Resume
          </a>
        ) : null}
        {email ? (
          <a
            href={`mailto:${email}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-2 text-xs font-medium text-ink transition-colors hover:bg-rail"
          >
            <EnvelopeSimple size={12} weight="bold" /> Send Mail
          </a>
        ) : null}
        {values.ctaLabel && values.ctaHref ? (
          <a
            href={values.ctaHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-2 text-xs font-medium text-ink transition-colors hover:bg-rail"
          >
            {values.ctaLabel}
            <ArrowUpRight size={12} weight="bold" />
          </a>
        ) : null}
        <span
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${
            values.availability === 'available'
              ? 'border-green-600/30 bg-green-50 text-green-900'
              : values.availability === 'limited'
                ? 'border-amber-600/30 bg-amber-50 text-amber-900'
                : 'border-line bg-rail text-ink-muted'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              values.availability === 'available'
                ? 'bg-green-600 animate-pulse'
                : values.availability === 'limited'
                  ? 'bg-amber-600'
                  : 'bg-ink-faint'
            }`}
          />
          {values.availability === 'available'
            ? 'Available for work'
            : values.availability === 'limited'
              ? 'Selective inquiries'
              : 'Not taking work'}
        </span>
      </div>
    </section>
  );
}

function AboutBlock({ values }: { values: AboutValues }) {
  return (
    <section>
      <SectionNumber num="01" title="About" />
      <p className="max-w-[62ch] whitespace-pre-line font-sans text-base leading-relaxed text-ink">
        {values.body}
      </p>
      {values.skills.length ? (
        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint">Skills & Tools</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {values.skills.map((s) => (
              <span
                key={s}
                className="rounded-md border border-line bg-board px-2 py-1 text-[11px] text-ink"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ProjectsBlock({ values }: { values: ProjectsValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionNumber num="02" title="Projects" />
      <div className="space-y-6">
        {values.items.map((p, idx) => (
          <article
            key={p.id}
            className="rounded-lg border border-line bg-board p-5 transition-colors hover:border-ink/30"
          >
            <div className="flex items-start gap-2 text-[11px] uppercase tracking-[0.15em] text-ink-faint">
              <span>#{String(idx + 1).padStart(2, '0')}</span>
              <span>·</span>
              <span>{p.year}</span>
              {p.role ? (
                <>
                  <span>·</span>
                  <span>{p.role}</span>
                </>
              ) : null}
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-4">
              <h3 className="font-sans text-lg font-semibold tracking-tight text-ink">{p.title}</h3>
              {p.url ? (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-ink"
                >
                  open <ArrowUpRight size={11} weight="bold" />
                </a>
              ) : null}
            </div>
            {p.mediaKind !== 'none' && p.mediaUrl ? (
              <div className="mt-3 overflow-hidden rounded-md border border-line bg-canvas">
                {p.mediaKind === 'image' ? (
                  <img
                    src={p.mediaUrl}
                    alt={p.mediaAlt ?? p.title}
                    className="aspect-[16/9] w-full object-cover"
                  />
                ) : (
                  <div className="grid aspect-[16/9] w-full place-items-center bg-rail text-[11px] text-ink-faint">
                    ▶ video · {p.mediaUrl}
                  </div>
                )}
              </div>
            ) : null}
            {p.summary ? (
              <p className="mt-3 max-w-[60ch] font-sans text-sm leading-relaxed text-ink-muted">
                {p.summary}
              </p>
            ) : null}
            {p.bullets && p.bullets.length ? (
              <ul className="mt-3 space-y-1.5 font-sans text-sm text-ink-muted">
                {p.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-ink-faint">▸</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {p.tags && p.tags.length ? (
              <div className="mt-3 flex flex-wrap gap-1">
                {p.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded border border-line bg-canvas px-1.5 py-0.5 text-[10px] text-ink-muted"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function ExperienceBlock({ values }: { values: ExperienceValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionNumber num="03" title="Experience" />
      <div className="space-y-6">
        {values.items.map((e) => (
          <div key={e.id} className="border-l-2 border-line pl-5">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-sans text-base font-semibold tracking-tight text-ink">
                {e.role} <span className="text-ink-muted">@ {e.company}</span>
              </h3>
              <span className="text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                {e.start} → {e.current ? 'now' : e.end}
              </span>
            </div>
            {e.location ? (
              <p className="mt-0.5 text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                {e.location}
              </p>
            ) : null}
            {e.summary ? (
              <p className="mt-2 max-w-[60ch] font-sans text-sm leading-relaxed text-ink-muted">
                {e.summary}
              </p>
            ) : null}
            {e.highlights && e.highlights.length ? (
              <ul className="mt-3 space-y-1.5 font-sans text-sm text-ink-muted">
                {e.highlights.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-ink-faint">▸</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function EducationBlock({ values }: { values: EducationValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionNumber num="04" title="Education" />
      <div className="space-y-5">
        {values.items.map((e) => (
          <div key={e.id} className="rounded-md border border-line bg-board p-4">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-sans text-base font-semibold tracking-tight text-ink">
                {e.institution}
              </h3>
              <span className="text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                {e.startYear}
                {e.endYear ? ` – ${e.endYear}` : ''}
              </span>
            </div>
            <p className="mt-0.5 font-sans text-sm text-ink-muted">
              {e.credential}
              {e.gpa ? ` · GPA ${e.gpa}` : ''}
            </p>
            {e.note ? (
              <p className="mt-2 max-w-[60ch] font-sans text-sm text-ink-muted">{e.note}</p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactBlock({ values }: { values: ContactValues }) {
  return (
    <section>
      <SectionNumber num="05" title="Contact" />
      <div className="rounded-lg border border-line bg-board p-5">
        <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          $ cat contact.txt
        </p>
        <div className="mt-4 space-y-3 font-sans text-sm">
          {values.email ? (
            <p>
              <span className="text-ink-faint">email </span>
              <a
                href={`mailto:${values.email}`}
                className="text-ink underline underline-offset-4"
              >
                {values.email}
              </a>
            </p>
          ) : null}
          {values.phone ? (
            <p>
              <span className="text-ink-faint">phone </span>
              <a
                href={`tel:${values.phone.replace(/\s+/g, '')}`}
                className="text-ink underline underline-offset-4"
              >
                {values.phone}
              </a>
            </p>
          ) : null}
          {values.location ? (
            <p>
              <span className="text-ink-faint">location </span>
              <span className="text-ink-muted">{values.location}</span>
            </p>
          ) : null}
        </div>
        {values.links.length ? (
          <ul className="mt-5 grid gap-2 sm:grid-cols-2">
            {values.links.map((l) => {
              const meta = PLATFORM_META[l.platform] ?? PLATFORM_META.custom;
              const Icon = meta.icon;
              const href =
                l.platform === 'email' && !l.href.startsWith('mailto:') ? `mailto:${l.href}` : l.href;
              return (
                <li key={l.id}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex w-full items-center gap-2 rounded-md border border-line bg-canvas px-3 py-2 font-sans text-sm font-medium text-ink transition-colors hover:bg-rail"
                  >
                    <Icon size={14} weight="regular" className="text-ink-muted" />
                    <span>{l.label || meta.label}</span>
                    <ArrowUpRight size={12} weight="bold" className="ml-auto text-ink-faint" />
                  </a>
                </li>
              );
            })}
          </ul>
        ) : null}
        {values.allowInquiryForm ? (
          <div className="mt-5 rounded-md border border-dashed border-line bg-canvas p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint">
              $ ./send-inquiry
            </p>
            <p className="mt-2 font-sans text-sm text-ink-muted">
              A short inquiry form renders here on your live site.
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
